/**
 * cloudinary.ts
 *
 * Mirrors the mobile CloudinaryService.
 *
 * Flow:
 *  1. POST /event/cloudinary/signature/  → { signature, timestamp, api_key, cloud_name,
 *                                            folder, upload_preset, upload_url }
 *  2. POST upload_url (FormData)         → { secure_url, public_id, ... }
 *
 * Usage:
 *  const urls = await uploadFilesToCloudinary(files, {
 *    folder: 'service_images',
 *    uploadPreset: 'service_preset',
 *  });
 */

import { apiClient } from '../api/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CloudinarySignature {
  signature: string;
  timestamp: number;
  api_key: string;
  cloud_name: string;
  folder: string;
  upload_preset: string;
  upload_url: string;
}

export interface CloudinaryUploadResult {
  public_id: string;
  secure_url: string;
  resource_type: string;
  bytes: number;
  format: string;
}

export interface UploadOptions {
  /** Cloudinary folder, e.g. 'service_images', 'event_images' */
  folder: string;
  /** Upload preset configured in your Cloudinary dashboard */
  uploadPreset: string;
  /** Resource type depending on what you are uploading: auto, image, video, raw, document */
  resourceType?: 'auto' | 'image' | 'video' | 'raw' | 'document';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Fetch a signed upload ticket from the backend.
 * Mirrors mobile getUploadSignature().
 */
async function getUploadSignature(
  folder: string,
  uploadPreset: string,
  fileCount: number,
  resourceType?: string,
): Promise<CloudinarySignature> {
  const { data } = await apiClient.post<CloudinarySignature>('/event/cloudinary/signature/', {
    folder,
    upload_preset: uploadPreset,
    file_count: fileCount,
    resource_type: resourceType || 'auto',
  });
  return data;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Upload an array of Files to Cloudinary and return their full result objects.
 *
 * @param files   Local File objects to upload
 * @param options folder + uploadPreset
 * @returns       Array of CloudinaryUploadResult in the same order as `files`
 */
export async function uploadFilesToCloudinary(
  files: File[],
  options: UploadOptions & { onProgress?: (percent: number) => void },
): Promise<CloudinaryUploadResult[]> {
  if (files.length === 0) return [];

  const sig = await getUploadSignature(
    options.folder,
    options.uploadPreset,
    files.length,
    options.resourceType,
  );

  const results = await Promise.all(
    files.map(async (f) => {
      const form = new FormData();
      form.append('file', f);
      form.append('api_key', sig.api_key);
      form.append('timestamp', String(sig.timestamp));
      form.append('signature', sig.signature);
      form.append('folder', sig.folder);

      return new Promise<CloudinaryUploadResult>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', sig.upload_url);

        if (options.onProgress && xhr.upload) {
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const percent = Math.round((e.loaded / e.total) * 100);
              options.onProgress?.(percent);
            }
          };
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result: CloudinaryUploadResult = JSON.parse(xhr.responseText);
              resolve(result);
            } catch {
              reject(new Error('Failed to parse Cloudinary response'));
            }
          } else {
            reject(new Error(`Cloudinary upload failed (${xhr.status}): ${xhr.responseText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error during Cloudinary upload'));
        xhr.send(form);
      });
    }),
  );

  return results;
}

/**
 * Upload and return only secure_url strings (for simpler use cases).
 */
export async function uploadFilesToCloudinarySimple(
  files: File[],
  options: UploadOptions,
): Promise<string[]> {
  const results = await uploadFilesToCloudinary(files, options);
  return results.map((r) => r.secure_url);
}
